import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2 } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WishlistPage = () => {
  const { items: wishlistItems, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    SEO.applyNoIndex('My Wishlist | PosterFire', '/wishlist');
    return () => SEO.resetDefaults();
  }, []);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      if (wishlistItems.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const fetched = await Promise.all(
          wishlistItems.map(item =>
            axios.get(`${API}/products/${item.id}`).then(r => r.data).catch(() => null)
          )
        );
        setProducts(fetched.filter(Boolean));
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [wishlistItems]);

  const handleAddToCart = async (product) => {
    try {
      await addToCart(product.id, 1, '18x24');
      toast.success('Added to cart!');
    } catch {
      toast.error('Failed to add to cart');
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromWishlist(productId);
      toast.success('Removed from wishlist');
    } catch {
      toast.error('Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="container-main section-padding" data-testid="wishlist-loading">
        <h1 className="font-heading text-3xl md:text-4xl mb-8">Your Wishlist</h1>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="aspect-[3/4] w-full" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-6 w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="container-main section-padding text-center" data-testid="wishlist-empty">
        <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
        <h1 className="font-heading text-3xl md:text-4xl mb-4">Your Wishlist is Empty</h1>
        <p className="text-muted-foreground mb-8">Save items you love for later.</p>
        <Link to="/shop" className="btn-primary">
          Explore Posters
        </Link>
      </div>
    );
  }

  return (
    <div className="container-main section-padding" data-testid="wishlist-page">
      <h1 className="font-heading text-3xl md:text-4xl mb-8">Your Wishlist ({products.length} items)</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
        {products.map((product) => (
          <div key={product.id} className="group" data-testid={`wishlist-item-${product.id}`}>
            <div className="relative overflow-hidden bg-secondary">
              <Link to={product.slug ? `/products/${product.slug}` : `/product/${product.id}`}>
                <img
                  src={product.image_url}
                  alt={`${product.title} ${product.category || ''} poster`}
                  className="w-full aspect-[3/4] object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </Link>
              <button
                onClick={() => handleRemove(product.id)}
                className="absolute top-4 right-4 p-2 bg-white/90 text-destructive hover:bg-destructive hover:text-white transition-all"
                data-testid={`remove-wishlist-${product.id}`}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-4 space-y-2">
              <span className="category-badge">{product.category}</span>
              <Link 
                to={product.slug ? `/products/${product.slug}` : `/product/${product.id}`}
                className="product-title block hover:text-fire transition-colors"
              >
                {product.title}
              </Link>
              <p className="product-price">${product.price?.toFixed(2)}</p>
              <button
                onClick={() => handleAddToCart(product)}
                className="w-full flex items-center justify-center gap-2 py-3 text-xs font-bold text-white rounded-lg bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 transition-all"
                data-testid={`add-cart-${product.id}`}
              >
                <ShoppingCart className="w-4 h-4" />
                Add to Cart
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WishlistPage;
